import type { TFunction } from 'i18next';

import { formatMoney, formatPrice } from './components/site.js';
import { intlLocale, toLanguage } from './i18n.js';

/**
 * Bounds of the numbers the forms send, as the API's schemas take them: `INPUT_LIMITS` of
 * packages/shared (SP-A2.232), a test holds this copy to it. Outside them the API answers only
 * «Bitte prüfen Sie Ihre Eingaben.», so the field names the bound before anything is sent.
 */
export const INPUT_LIMITS = {
  hourlyRate: { min: 10, max: 500 },
  monthlyVisitors: { min: 0, max: 100_000_000 },
  conversionPct: { min: 0, max: 100 },
  avgOrderEur: { min: 0, max: 1_000_000 },
  servicePriceCents: { min: 0, max: 10_000_000 },
  serviceSortOrder: { min: 0, max: 1000 },
  invoiceNetCents: { min: 0, max: 10_000_000 },
  requestPriceCents: { min: 0, max: 100_000_000 },
} as const;

export interface Bound {
  readonly min: number;
  readonly max: number;
}

/**
 * What a field reads: an amount in cents or in euros, a count, a share in per cent, or a whole
 * number of a short range, as a service's place in the list (SP-A2.244).
 */
export type NumberUnit = 'cents' | 'euros' | 'count' | 'percent' | 'integer';

/**
 * An amount as the field reads it back: as prices are shown («100.000 €», «100 000 €»), in
 * English with cents, as its hint writes them («1,234.50») – English thousands without cents
 * («€100,000») the field refuses, «1,234» could be 1,23 € (review of SP-A2.232).
 */
const amount = (cents: number, language: string) =>
  toLanguage(language) === 'en' ? formatMoney(cents, language) : formatPrice(cents, language);

/**
 * A bound as the field names it: amounts as the field reads them, counts in words («100
 * Millionen» – «100,000,000» would read as a count the field refuses in English), shares with «%»,
 * a whole number of a short range in digits without thousands («1000»: «1 Tausend» reads oddly,
 * «1,000» the field refuses in English).
 */
export function boundText(unit: NumberUnit, value: number, language: string) {
  const locale = intlLocale(language);
  if (unit === 'integer') return String(value);
  if (unit === 'cents') return amount(value, language);
  if (unit === 'euros') return amount(value * 100, language);
  if (unit === 'count')
    return new Intl.NumberFormat(locale, { notation: 'compact', compactDisplay: 'long' }).format(
      value,
    );
  return new Intl.NumberFormat(locale, { style: 'percent' }).format(value / 100);
}

/**
 * What a field says of the number read from it: one it cannot read (undefined) – how to write it
 * (SP-A2.222); one outside the API's bound – the bound (SP-A2.232); else, and of none (null),
 * nothing. Counts and shares start at 0: only their upper bound is named. A whole number of a
 * short range is told its range either way – «-5» is a whole number, and no example of how to
 * write one («1.500») fits a range up to 1000 (SP-A2.244).
 */
export function numberError(
  value: number | null | undefined,
  unit: NumberUnit,
  bound: Bound,
  t: TFunction,
  language: string,
): string | undefined {
  if (value === null || (value !== undefined && value >= bound.min && value <= bound.max))
    return undefined;
  const max = boundText(unit, bound.max, language);
  if (unit === 'integer')
    return t('app.integerRange', { min: boundText(unit, bound.min, language), max });
  if (unit === 'count')
    return value === undefined ? t('app.countInvalid') : t('app.countMax', { max });
  if (unit === 'percent')
    return value === undefined ? t('app.percentInvalid') : t('app.percentMax', { max });
  if (value === undefined) return t('app.amountInvalid');
  return bound.min > 0
    ? t('app.amountRange', { min: boundText(unit, bound.min, language), max })
    : t('app.amountMax', { max });
}
